import { useState } from "react";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import { MetricCard } from "../../../components/PromotionIQ/MetricCard";
import {
  retailers,
  historicalPromotions,
  formatCurrency,
  formatPercent,
  getSkuInfo,
  getRetailerById,    
} from "../../../data/PromotionIQ/PromotionData";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import React from "react";  

const RetailerPromotionTruth = () => {
  const [selectedRetailer, setSelectedRetailer] = useState("R1");
  const retailer = getRetailerById(selectedRetailer);

  const retailerPromos = historicalPromotions.filter(p => p.retailer_id === selectedRetailer);

  const totalSpend = retailerPromos.reduce((sum, p) => sum + p.trade_spend, 0);
  const totalRevenue = retailerPromos.reduce((sum, p) => sum + p.incremental_revenue, 0);
  const totalMargin = retailerPromos.reduce((sum, p) => sum + p.net_margin, 0);

  const hasPromos = retailerPromos.length > 0;

  // Volume decomposition aggregate
  const avgVolumeSplit = {
    incremental_consumption: hasPromos
      ? retailerPromos.reduce((sum, p) => sum + p.volume_split.incremental_consumption, 0) / retailerPromos.length
      : 0,
    pantry_loading: hasPromos
      ? retailerPromos.reduce((sum, p) => sum + p.volume_split.pantry_loading, 0) / retailerPromos.length
      : 0,
    forward_buying: hasPromos
      ? retailerPromos.reduce((sum, p) => sum + p.volume_split.forward_buying, 0) / retailerPromos.length
      : 0,
    subsidized_base: hasPromos
      ? retailerPromos.reduce((sum, p) => sum + p.volume_split.subsidized_base, 0) / retailerPromos.length
      : 0,
  };

  const volumeData = [
    { name: "Incremental Consumption", value: avgVolumeSplit.incremental_consumption, color: "hsl(142, 70%, 45%)" },
    { name: "Pantry Loading", value: avgVolumeSplit.pantry_loading, color: "hsl(45, 93%, 47%)" },
    { name: "Forward Buying", value: avgVolumeSplit.forward_buying, color: "hsl(32, 95%, 55%)" },
    { name: "Subsidized Base", value: avgVolumeSplit.subsidized_base, color: "hsl(0, 72%, 51%)" },
  ];

  // Timing effects aggregate
  const avgTimingEffects = {
    pre: hasPromos
      ? retailerPromos.reduce((sum, p) => sum + p.timing_effects.pre_promo_lift, 0) / retailerPromos.length
      : 0,
    in: hasPromos
      ? retailerPromos.reduce((sum, p) => sum + p.timing_effects.in_promo_lift, 0) / retailerPromos.length
      : 0,
    post: hasPromos
      ? retailerPromos.reduce((sum, p) => sum + p.timing_effects.post_promo_dip, 0) / retailerPromos.length
      : 0,
  };

  const timingData = [
    { phase: "Pre-Promo", lift: avgTimingEffects.pre * 100 },
    { phase: "In-Promo", lift: avgTimingEffects.in * 100 },
    { phase: "Post-Promo", lift: avgTimingEffects.post * 100 },
  ];

  // Spend vs Revenue chart
  const spendRevenueData = retailerPromos.map(p => ({
    id: p.promotion_id,
    spend: p.trade_spend,
    revenue: p.incremental_revenue,
    margin: p.net_margin,
  }));

  return (
    <div className="space-y-6">
      <ScreenHeader
        title="Retailer Promotion Truth"
        question="What really happened when we promoted here?"
      />

      {/* Retailer Selector */}
      <div className="border border-border bg-card p-4">
        <label className="label-athena mb-2 block">Select Retailer</label>
        <select
          value={selectedRetailer}
          onChange={(e) => setSelectedRetailer(e.target.value)}
          className="input-athena max-w-md"
        >
          {retailers.map((r) => (
            <option key={r.retailer_id} value={r.retailer_id}>
              {r.name}
            </option>
          ))}
        </select>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <MetricCard
          label="Trade Spend"
          value={formatCurrency(totalSpend)}
          subValue="Total investment"
          variant="default"
        />
        <MetricCard
          label="Incremental Revenue"
          value={formatCurrency(totalRevenue)}
          subValue="Generated revenue"
          variant="highlight"
        />
        <MetricCard
          label="Net Margin Impact"
          value={formatCurrency(totalMargin)}
          subValue="Our margin (not retailer's)"
          variant={totalMargin > 0 ? "success" : "warning"}
        />
        <MetricCard
          label="ROI"
          value={`${totalSpend > 0 ? (totalRevenue / totalSpend).toFixed(2) : "0.00"}x`}
          subValue="Revenue per $1 spent"
          variant={totalSpend > 0 && totalRevenue / totalSpend >= 1.2 ? "success" : "default"}
        />
      </div>

      {!hasPromos && (
        <div className="border border-border bg-card p-6">
          <p className="text-sm text-muted-foreground">
            No historical promotions found for this retailer. Select another retailer to view truth diagnostics.
          </p>
        </div>
      )}

      {/* Charts Row */}
      {hasPromos && (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Spend vs Revenue */}
        <div className="border border-border bg-card p-6">
          <h3 className="section-header mb-4">Trade Spend vs Incremental Revenue</h3>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spendRevenueData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 15%, 25%)" />
                <XAxis dataKey="id" stroke="hsl(220, 10%, 55%)" fontSize={11} />
                <YAxis stroke="hsl(220, 10%, 55%)" fontSize={11} tickFormatter={(v) => `$${v / 1000}K`} />
                <Tooltip
                  contentStyle={{ backgroundColor: "hsl(220, 18%, 13%)", border: "1px solid hsl(220, 15%, 25%)", borderRadius: "0" }}
                  formatter={(value: number) => formatCurrency(value)}
                />
                <Bar dataKey="spend" fill="hsl(220, 10%, 40%)" name="Trade Spend" />
                <Bar dataKey="revenue" fill="hsl(32, 95%, 55%)" name="Incr. Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Volume Decomposition */}
        <div className="border border-border bg-card p-6">
          <h3 className="section-header mb-4">Volume Decomposition</h3>
          <div className="flex items-center gap-4">
            <div className="h-[200px] w-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={volumeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    paddingAngle={2}
                  >
                    {volumeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatPercent(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {volumeData.map((item) => (
                <div key={item.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3" style={{ backgroundColor: item.color }} />
                    <span className="text-muted-foreground">{item.name}</span>
                  </div>
                  <span className="font-bold">{formatPercent(item.value)}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Only <span className="text-success font-bold">{formatPercent(avgVolumeSplit.incremental_consumption)}</span> is true incremental consumption. 
            The rest is timing shifts or subsidizing sales that would have happened anyway.
          </p>
        </div>
      </div>
      )}

      {/* Timing Effects */}
      <div className="border border-border bg-card p-6">
        <h3 className="section-header mb-4">Timing Effects</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className={`text-center p-6 ${avgTimingEffects.pre > 0 ? "bg-success/10" : "bg-muted/30"}`}>
            <p className="text-3xl font-bold">{avgTimingEffects.pre > 0 ? "+" : ""}{(avgTimingEffects.pre * 100).toFixed(0)}%</p>
            <p className="text-muted-foreground mt-2">Pre-Promo Lift</p>
            <p className="text-xs text-muted-foreground">Anticipation buying</p>
          </div>
          <div className="text-center p-6 bg-primary/10">
            <p className="text-3xl font-bold text-primary">+{(avgTimingEffects.in * 100).toFixed(0)}%</p>
            <p className="text-muted-foreground mt-2">In-Promo Lift</p>
            <p className="text-xs text-muted-foreground">During promotion</p>
          </div>
          <div className={`text-center p-6 ${avgTimingEffects.post < 0 ? "bg-destructive/10" : "bg-muted/30"}`}>
            <p className="text-3xl font-bold text-destructive">{(avgTimingEffects.post * 100).toFixed(0)}%</p>
            <p className="text-muted-foreground mt-2">Post-Promo Dip</p>
            <p className="text-xs text-muted-foreground">Payback period</p>
          </div>
        </div>
      </div>

      {/* Promotion Details */}
      <div className="border border-border bg-card p-6">
        <h3 className="section-header mb-4">Historical Promotions at {retailer?.name}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-muted-foreground">Promo ID</th>
                <th className="text-left py-3 px-4 text-muted-foreground">Product</th>
                <th className="text-left py-3 px-4 text-muted-foreground">Period</th>
                <th className="text-right py-3 px-4 text-muted-foreground">Spend</th>
                <th className="text-right py-3 px-4 text-muted-foreground">Revenue</th>
                <th className="text-right py-3 px-4 text-muted-foreground">Margin</th>
                <th className="text-right py-3 px-4 text-muted-foreground">vs Plan</th>
              </tr>
            </thead>
            <tbody>
              {retailerPromos.map((p) => (
                <tr key={p.promotion_id} className="border-b border-border/50 hover:bg-muted/20">
                  <td className="py-3 px-4 font-medium">{p.promotion_id}</td>
                  <td className="py-3 px-4">{getSkuInfo(p.sku_id)}</td>
                  <td className="py-3 px-4 text-muted-foreground">{p.period}</td>
                  <td className="py-3 px-4 text-right">{formatCurrency(p.trade_spend)}</td>
                  <td className="py-3 px-4 text-right text-primary">{formatCurrency(p.incremental_revenue)}</td>
                  <td className={`py-3 px-4 text-right ${p.net_margin >= 0 ? "text-success" : "text-destructive"}`}>
                    {formatCurrency(p.net_margin)}
                  </td>
                  <td className={`py-3 px-4 text-right ${(p.actual_vs_planned || 0) >= 0 ? "text-success" : "text-destructive"}`}>
                    {(p.actual_vs_planned || 0) >= 0 ? "+" : ""}{p.actual_vs_planned?.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Key Insight */}
      <div className="border border-primary/50 bg-primary/5 p-6">
        <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">Purpose</p>
        <p className="text-lg">
          This screen <span className="text-primary font-bold">destroys false wins</span> and establishes economic truth reconciled to Finance. 
          Not all promoted volume is incremental – understanding the split is critical.
        </p>
      </div>
    </div>
  );
};

export default RetailerPromotionTruth;
